import React from 'react';
import { Link } from 'react-router-dom';

export default function Terms() {
  return (
    <>
      <title>Terms of Service – ReStaff</title>
      <div className="min-h-screen bg-white">
        <div className="max-w-3xl mx-auto px-6 py-16 md:py-20">

          {/* Logo */}
          <div className="mb-12">
            <Link to="/" className="inline-flex items-center gap-2">
              <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold">RS</span>
              </div>
              <span className="font-bold text-xl text-slate-900" style={{ fontFamily: 'Manrope' }}>ReStaff</span>
            </Link>
          </div>

          {/* Header */}
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
            Terms of Service
          </h1>
          <p className="text-slate-500 text-sm mb-2">ReStaff – GetReStaff.co</p>
          <p className="text-slate-400 text-sm mb-12">Effective Date: 12 February 2026</p>

          <div className="prose prose-slate max-w-none space-y-10 text-sm text-slate-700 leading-relaxed">

            <section>
              <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Agreement &amp; Business Use</h2>
              <p>These Terms of Service ("Terms") govern access to and use of the ReStaff website located at <a href="https://getrestaff.co" className="text-orange-600 hover:underline">https://getrestaff.co</a> and the ReStaff workforce management software (collectively, the "Service").</p>
              <p className="mt-3">The Service is owned and operated by VoyAsia LLC, a Wyoming limited liability company ("Company," "ReStaff," "we," "us," "our").</p>
              <p className="mt-3">The Service is offered strictly for commercial and business use. By using the Service, you represent that you are a business entity or acting on behalf of one.</p>
              <p className="mt-3">If you do not agree, you must not use the Service.</p>
            </section>

            <section>
              <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Definitions</h2>
              <ul className="list-disc list-inside space-y-1.5 text-slate-700">
                <li><strong>"Customer"</strong> means the business subscribing to the Service.</li>
                <li><strong>"Authorized Users"</strong> means Customer's employees or contractors.</li>
                <li><strong>"Customer Data"</strong> means all data uploaded to the Service.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">License</h2>
              <p>Subject to payment and compliance, ReStaff grants Customer a limited, non-exclusive, non-transferable, revocable license to access and use the Service for internal business operations only.</p>
              <p className="mt-3">No resale, sublicensing, or white-label use is permitted without written consent.</p>
            </section>

            <section>
              <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Account &amp; Security</h2>
              <p>Customer must:</p>
              <ul className="list-disc list-inside space-y-1.5 mt-2">
                <li>Provide accurate information</li>
                <li>Maintain credential security</li>
                <li>Control access for Authorized Users</li>
              </ul>
              <p className="mt-3">Customer is responsible for all activity under its account.</p>
            </section>

            <section>
              <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Free Trial</h2>
              <p>ReStaff may offer a free trial.</p>
              <ul className="list-disc list-inside space-y-1.5 mt-2">
                <li>If a payment method is provided at signup, subscription automatically converts to paid at the end of the trial unless canceled before expiration.</li>
                <li>If no payment method is required, access may terminate at trial end unless upgraded.</li>
                <li>We may limit one trial per business.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Fees, Billing &amp; Stripe Payments</h2>

              <h3 className="text-sm font-semibold text-slate-800 mt-4 mb-2">6.1 Recurring Billing</h3>
              <p>Subscriptions are billed monthly or annually in advance. By subscribing, Customer authorizes recurring charges until cancellation.</p>

              <h3 className="text-sm font-semibold text-slate-800 mt-4 mb-2">6.2 Payment Processing</h3>
              <p>Payments are processed by third-party payment processors, including Stripe. ReStaff does not store full payment card numbers. Customer agrees to comply with Stripe's applicable terms and authorizes us to share necessary billing data with our payment processor.</p>

              <h3 className="text-sm font-semibold text-slate-800 mt-4 mb-2">6.3 Failed Payments</h3>
              <p>If payment fails:</p>
              <ul className="list-disc list-inside space-y-1.5 mt-2">
                <li>We may retry charges</li>
                <li>We may suspend access</li>
                <li>Late balances remain payable</li>
              </ul>

              <h3 className="text-sm font-semibold text-slate-800 mt-4 mb-2">6.4 No Refund Policy</h3>
              <p>Except where required by law:</p>
              <ul className="list-disc list-inside space-y-1.5 mt-2">
                <li>All payments are final</li>
                <li>No refunds for partial periods</li>
                <li>No refunds for unused services</li>
                <li>No refunds for downtime</li>
                <li>No refunds for suspension due to violation</li>
              </ul>

              <h3 className="text-sm font-semibold text-slate-800 mt-4 mb-2">6.5 Chargebacks</h3>
              <p>Initiating a chargeback constitutes a material breach. We may immediately suspend the account and recover associated fees and costs.</p>

              <h3 className="text-sm font-semibold text-slate-800 mt-4 mb-2">6.6 Taxes</h3>
              <p>Fees exclude applicable taxes. Customer is responsible for all taxes.</p>
            </section>

            <section>
              <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Customer Responsibilities</h2>
              <p>Customer is solely responsible for:</p>
              <ul className="list-disc list-inside space-y-1.5 mt-2">
                <li>Labor law compliance</li>
                <li>Payroll accuracy</li>
                <li>Employee classification</li>
                <li>Overtime compliance</li>
                <li>Regulatory filings</li>
              </ul>
              <p className="mt-3">ReStaff provides informational workforce tools only and does not provide legal, payroll, or compliance advice. Customer bears responsibility for decisions made using the Service.</p>
            </section>

            <section>
              <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Customer Data &amp; Processing</h2>
              <ul className="list-disc list-inside space-y-1.5">
                <li>Customer retains ownership of Customer Data.</li>
                <li>Customer grants ReStaff a limited license to host and process data solely to provide the Service.</li>
                <li>Customer represents it has lawful authority to process employee data.</li>
                <li>ReStaff acts as Data Processor; Customer acts as Data Controller.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Data Processing Addendum (DPA)</h2>
              <p>Where Customer processes personal data subject to GDPR or UK GDPR:</p>
              <ul className="list-disc list-inside space-y-1.5 mt-2">
                <li>ReStaff processes data only on documented instructions</li>
                <li>Security measures include encryption in transit and access controls</li>
                <li>Subprocessors (hosting, email, billing) may be used</li>
                <li>International transfers rely on lawful mechanisms such as Standard Contractual Clauses</li>
                <li>ReStaff will notify Customer without undue delay of confirmed security incidents</li>
                <li>Data will be deleted or returned upon termination, subject to legal retention requirements</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Prohibited Use</h2>
              <p>Customer may not:</p>
              <ul className="list-disc list-inside space-y-1.5 mt-2">
                <li>Reverse engineer the software</li>
                <li>Bypass security</li>
                <li>Conduct unauthorized testing</li>
                <li>Share credentials</li>
                <li>Use the Service unlawfully</li>
              </ul>
              <p className="mt-3">Violation may result in termination without refund.</p>
            </section>

            <section>
              <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Service Availability</h2>
              <p>The Service is provided "AS IS" and "AS AVAILABLE." We do not guarantee uninterrupted service. Maintenance and outages may occur.</p>
            </section>

            <section>
              <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Service Level Agreement (SLA)</h2>
              <p>ReStaff targets 99.5% monthly uptime, excluding:</p>
              <ul className="list-disc list-inside space-y-1.5 mt-2">
                <li>Scheduled maintenance</li>
                <li>Force majeure</li>
                <li>Third-party outages</li>
              </ul>
              <p className="mt-3">If uptime falls below 99.5%:</p>
              <ul className="list-disc list-inside space-y-1.5 mt-2">
                <li>99.0–99.49% → 5% credit</li>
                <li>98.0–98.99% → 10% credit</li>
                <li>Below 98.0% → 15% credit</li>
              </ul>
              <p className="mt-3">Credits apply to future invoices only and are Customer's sole remedy.</p>
            </section>

            <section>
              <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Disclaimer of Warranties</h2>
              <p>To the maximum extent permitted by law, the Service is provided without warranties of any kind, including merchantability, fitness for a particular purpose, or non-infringement.</p>
            </section>

            <section>
              <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Limitation of Liability</h2>
              <p>To the fullest extent permitted by law:</p>
              <ul className="list-disc list-inside space-y-1.5 mt-2">
                <li>ReStaff shall not be liable for indirect, incidental, consequential, punitive, or special damages.</li>
                <li>Total aggregate liability shall not exceed the greater of fees paid in the 12 months preceding the claim, or $500 USD.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Indemnification</h2>
              <p>Customer shall defend and indemnify ReStaff against claims arising from:</p>
              <ul className="list-disc list-inside space-y-1.5 mt-2">
                <li>Customer Data</li>
                <li>Labor violations</li>
                <li>Employee disputes</li>
                <li>Misuse of the Service</li>
                <li>Breach of these Terms</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Termination</h2>
              <p>We may suspend or terminate accounts for:</p>
              <ul className="list-disc list-inside space-y-1.5 mt-2">
                <li>Non-payment</li>
                <li>Violation of Terms</li>
                <li>Fraud or legal risk</li>
              </ul>
              <p className="mt-3">Termination does not relieve Customer of outstanding payment obligations.</p>
            </section>

            <section>
              <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Governing Law &amp; Arbitration</h2>
              <p>These Terms are governed by Wyoming law. All disputes shall be resolved by binding arbitration administered by the American Arbitration Association under Commercial Rules.</p>
              <ul className="list-disc list-inside space-y-1.5 mt-2">
                <li>Class actions are waived.</li>
                <li>Claims must be brought individually.</li>
                <li>Any claim must be filed within one (1) year.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Modifications</h2>
              <p>We may update these Terms at any time. Continued use after updates constitutes acceptance.</p>
            </section>

            <section>
              <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Contact</h2>
              <p>VoyAsia LLC<br />ReStaff – <a href="https://getrestaff.co" className="text-orange-600 hover:underline">https://getrestaff.co</a></p>
              <p className="mt-2">Legal/support: <a href="mailto:tickets@restaff-n513b1.p.tawk.email" className="text-orange-600 hover:underline">tickets@restaff-n513b1.p.tawk.email</a></p>
            </section>

          </div>

          {/* Back link */}
          <div className="mt-16 pt-8 border-t border-slate-100">
            <a href="/" className="text-sm text-orange-600 hover:underline">← Back to ReStaff</a>
          </div>

        </div>
      </div>
    </>
  );
}
